import classNames from 'classnames';
import { toEssencePool, type EssencePool, type UpgradeStat } from '@sorrem/shared';
import { EssenceTally, UpgradePips } from '@sorrem-client/ui/components';
import type { UpgradePathRow } from '../../crafting-preview';
import styles from './UpgradePath.module.scss';

interface UpgradePathProps {
    readonly rows: readonly UpgradePathRow[];
    readonly currentLevel: number;
    /** The upgrade stats as the item is now, for the Current card. */
    readonly currentStats: readonly { readonly stat: UpgradeStat; readonly value: number }[];
    /** The level chosen: the item's own (the Current card) or one above it. */
    readonly targetLevel: number;
    readonly onChooseLevel: (level: number) => void;
}

const STAT_LABELS: Readonly<Record<UpgradeStat, string>> = {
    damage: 'Damage',
    accuracy: 'Accuracy',
    resistance: 'Resistance',
    defense: 'Defense',
    essencePool: 'Essence pool',
};

/** Whole numbers, as the item card shows them: damage keeps its catalog fraction underneath, but each level adds whole points. */
function formatStat(value: number): string {
    return Math.round(value).toLocaleString();
}

interface LevelCardProps {
    readonly title: string;
    readonly ariaLabel: string;
    readonly level: number;
    readonly stats: readonly { readonly stat: UpgradeStat; readonly value: number }[];
    /** What reaching the level costs from now; null on the Current card. */
    readonly total: Partial<EssencePool> | null;
    readonly isChosen: boolean;
    readonly isOnTheWay: boolean;
    readonly onChoose: () => void;
}

function LevelCard({ title, ariaLabel, level, stats, total, isChosen, isOnTheWay, onChoose }: LevelCardProps): JSX.Element {
    return (
        <li>
            <button
                type='button'
                className={classNames(styles.level, isChosen && styles.chosen, isOnTheWay && styles.onTheWay)}
                aria-pressed={isChosen}
                aria-label={ariaLabel}
                onClick={onChoose}
            >
                <span className={styles.head}>
                    <span className={styles.levelName}>{title}</span>
                    <UpgradePips level={level} />
                </span>
                <span className={styles.stats}>
                    {stats.map((entry) => (
                        <span key={entry.stat} className={styles.stat}>
                            <span>{STAT_LABELS[entry.stat]}</span>
                            <span className={styles.value}>{formatStat(entry.value)}</span>
                        </span>
                    ))}
                </span>
                {total !== null && (
                    <EssenceTally essencePool={toEssencePool(total)} ariaLabel={`Essence to reach +${level}`} label='Total' isEmptyHidden />
                )}
            </button>
        </li>
    );
}

/**
 * The item's upgrade levels as cards (#287), in a column beside it: the
 * Current card for the item as it is, then each level still above it,
 * with pips for the level, the stats there, and what reaching it costs
 * from now. Choosing a card shows the item at that level on the item's
 * card (its stats flashing with the change) and in the vessel panel.
 * Levels already reached are left out: the pips say how far it has come.
 */
export function UpgradePath({ rows, currentLevel, currentStats, targetLevel, onChooseLevel }: UpgradePathProps): JSX.Element {
    const ahead = rows.filter((row) => !row.isReached);
    return (
        <ul className={styles.upgradePath} aria-label='Upgrade levels'>
            <LevelCard
                title='Current'
                ariaLabel={`Current, +${currentLevel}`}
                level={currentLevel}
                stats={currentStats}
                total={null}
                isChosen={targetLevel === currentLevel}
                isOnTheWay={false}
                onChoose={() => onChooseLevel(currentLevel)}
            />
            {ahead.map((row) => (
                <LevelCard
                    key={row.level}
                    title={`+${row.level}`}
                    ariaLabel={`Upgrade to +${row.level}`}
                    level={row.level}
                    stats={row.stats}
                    total={row.total}
                    isChosen={row.level === targetLevel}
                    isOnTheWay={row.level < targetLevel}
                    onChoose={() => onChooseLevel(row.level)}
                />
            ))}
            {ahead.length === 0 && <li className={styles.note}>Fully upgraded.</li>}
        </ul>
    );
}
